import { lowerCase, partition } from 'lodash/fp';
import { FC } from 'react';
import styled from 'styled-components';

import { TableTD, TableTR } from 'dashboard/layouts/TableLayout';
import { getTriggerDescription } from 'dashboard/lib/workflows/trigger';
import {
  PermissionBlockedNode,
  PlatformConfigType,
  WorkflowNode,
} from 'dashboard/models/platform-node';
import { SupportedTriggerEvent } from 'dashboard/models/trigger';
import { NODE_TYPE_TO_LABEL } from 'dashboard/views/DashboardPlatformConnectionsShowView/constants';
import NullState from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/NullState';
import { PlatformNodeLink } from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/PlatformNodeLink';
import {
  PlatformNodeTable,
  PlatformNodeTableHeader,
} from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/PlatformNodeTable';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { ApiModel } from 'lib/interfaces/api';

const StyledCardTitle = styled(Card.Title)`
  margin: 0 !important;
`;

interface TableWorkflowNodeProps {
  nodes: (WorkflowNode | PermissionBlockedNode)[];
  rootNodeType: PlatformConfigType;
}

const TableWorkflowNode: FC<TableWorkflowNodeProps> = ({ nodes, rootNodeType }) => {
  const [workflowNodes, caseActionNodes] = partition(
    (workflowNode) =>
      workflowNode['permission-blocked'] ||
      workflowNode.metadata?.['trigger-type'] !== ApiModel.TriggerCase,
    nodes,
  );

  return (
    <>
      <WorkflowTableCard
        nodes={workflowNodes}
        tableName='Workflows'
        description={`This ${lowerCase(NODE_TYPE_TO_LABEL[rootNodeType])} can trigger the following workflows`}
        displayTrigger
      />
      {rootNodeType === PlatformConfigType.CaseTemplate && (
        <WorkflowTableCard
          nodes={caseActionNodes}
          tableName='Case Actions'
          description='This case template can trigger the following case actions'
        />
      )}
    </>
  );
};
interface WorkflowTableCardProps {
  tableName: string;
  nodes: (WorkflowNode | PermissionBlockedNode)[];
  description: string;
  displayTrigger?: boolean;
}

const WorkflowTableCard: FC<WorkflowTableCardProps> = ({
  description,
  displayTrigger,
  tableName,
  nodes,
}) => {
  return (
    <Card>
      <Card.Body>
        <StyledCardTitle>{tableName}</StyledCardTitle>
        <Card.Subtitle>{description}</Card.Subtitle>
        <PlatformNodeTable>
          <PlatformNodeTableHeader
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'id', label: 'ID' },
              ...(displayTrigger
                ? [
                    { key: 'filler', label: '' },
                    { key: 'trigger-type', label: 'Trigger' },
                  ]
                : []),
            ]}
          />
          {nodes.map((workflowNode, idx) => {
            return (
              <TableTR disableHoverBackground key={idx}>
                <TableTD>
                  {workflowNode['permission-blocked'] ? (
                    <Sticker variant='grey'>Restricted</Sticker>
                  ) : (
                    <PlatformNodeLink node={workflowNode} />
                  )}
                </TableTD>
                <TableTD>{workflowNode.token}</TableTD>
                {displayTrigger && !workflowNode['permission-blocked'] && (
                  <>
                    <TableTD />
                    <TableTD>
                      {workflowNode.metadata &&
                        getTriggerDescription(
                          workflowNode.metadata['trigger-type'],
                          workflowNode.metadata['event-name'] as SupportedTriggerEvent | undefined,
                        )}
                    </TableTD>
                  </>
                )}
              </TableTR>
            );
          })}
        </PlatformNodeTable>
        {!nodes.length && <NullState message='No workflow connections found' />}
      </Card.Body>
    </Card>
  );
};

export default TableWorkflowNode;
